/**
 * Пост на продажу: текст «Арт. + прокат от + призыв» и кадр под соцсеть.
 *
 * Модуль без импортов — его грузит `node --test`. Текст не хранится: собирается
 * заново из CRM при каждом открытии карточки; кадр режется в браузере.
 */

/** Призыв — константа фронта (формулировка SMM). */
export const POST_CALL = "Запишитесь на примерку в сообщениях";

/** Место для ручного ввода: нет серии или цены. */
const BLANK = "…";

/** `4900 → "4 900"`: разделитель тысяч — обычный пробел (его вставляют в соцсеть руками). */
export function formatRub(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function postText(series: string | null, price: number | null): string {
  const s = series?.trim() || BLANK;
  const p = price === null ? BLANK : formatRub(price);
  return `Арт. ${s}\nПрокат от ${p} ₽\n${POST_CALL}`;
}

/**
 * Имя модели CRM — только если в нём есть буква: `modelName` почти всегда тот же артикул
 * цифрами (иначе пусто) — это внутренний номер CRM, не название, показывать его рядом
 * с артикулом не нужно.
 */
export function modelLabel(name: string | null): string | null {
  return name && /\p{L}/u.test(name) ? name : null;
}

/**
 * Подпись под «Арт.»: платье привязано ДРУГОЙ серией (ключ привязки, обычно — серия
 * экземпляра), чем артикул CRM, показанный в тексте. Совпадают или артикула нет — без подписи.
 */
export function boundSeriesNote(series: string | null, boundSeries: string | null): string | null {
  return series && boundSeries && series !== boundSeries ? `привязано по серии ${boundSeries}` : null;
}

export type Aspect = "orig" | "4:5" | "1:1" | "9:16" | "3:4" | "2:3";

export const ASPECTS: { value: Aspect; label: string }[] = [
  { value: "orig", label: "как есть" },
  { value: "4:5", label: "4:5" },
  { value: "1:1", label: "1:1" },
  { value: "9:16", label: "9:16" },
];

/** Ширина к высоте. */
const RATIO: Record<Exclude<Aspect, "orig">, number> = {
  "4:5": 4 / 5,
  "1:1": 1,
  "9:16": 9 / 16,
  "3:4": 3 / 4,
  "2:3": 2 / 3,
};

export type Rect ={ x: number; y: number; w: number; h: number };

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/**
 * Наибольшая рамка пропорции внутри картинки `w×h`, центр в (`cx`,`cy`) — доли 0..1;
 * у края рамка прижимается и не выходит за картинку. `orig` — вся картинка.
 */
export function cropRect(w: number, h: number, aspect: Aspect, cx: number, cy: number): Rect {
  if (aspect === "orig") return { x: 0, y: 0, w, h };
  const ratio = RATIO[aspect];
  const cw = w / h > ratio ? Math.round(h * ratio) : w;
  const ch = w / h > ratio ? h : Math.round(w / ratio);
  return {
    x: clamp(Math.round(cx * w - cw / 2), 0, w - cw),
    y: clamp(Math.round(cy * h - ch / 2), 0, h - ch),
    w: cw,
    h: ch,
  };
}

/** Предел площади холста iOS Safari (16 777 216 px) с запасом: больше — холст молча пустой. */
export const EXPORT_MAX_PX = 16_700_000;

/**
 * Масштаб выхода `≤ 1`: кадр `w×h` больше `maxPx` по площади — пропорционально вниз;
 * стороны выхода — `Math.floor(w * s)`, `Math.floor(h * s)` (их произведение не больше `maxPx`).
 */
export function exportScale(w: number, h: number, maxPx: number): number {
  const area = w * h;
  return area > maxPx ? Math.sqrt(maxPx / area) : 1;
}

/** `unirenter-150102-4x5.jpg`; нет серии — `unirenter-v57-orig.jpg`. */
export function cropFileName(series: string | null, variantId: number, aspect: Aspect): string {
  const s = series?.trim().replace(/[^\w.-]+/g, "_");
  return `unirenter-${s || `v${variantId}`}-${aspect.replace(":", "x")}.jpg`;
}
